import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import Swiper, { Mousewheel } from "swiper";

Swiper.use([Mousewheel]);
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

class SolutionScrollAccordion {
	static DESKTOP_BREAKPOINT = 1024;

	static SWIPER_SPEED = 420;
	static WHEEL_THRESHOLD_DELTA = 14;
	static WHEEL_THRESHOLD_TIME = 140;
	static WHEEL_SENSITIVITY = 1.15;
	static GESTURE_GAP_MS = 140;
	static TRACKPAD_WHEEL_THRESHOLD_DELTA = 2;
	static TRACKPAD_WHEEL_THRESHOLD_TIME = 60;
	static TRACKPAD_WHEEL_SENSITIVITY = 0.95;
	static PIN_ARM_SPIKE_MIN_DELTA = 6;
	static PIN_ARM_SPIKE_MULT = 1.45;
	static STEP_LOCK_MIN_MS = 180;
	static STEP_LOCK_MAX_MS = 1200;

	static DESC_OPEN_DURATION = 0.55;
	static DESC_CLOSE_DURATION = 0.45;
	static MEDIA_FADE_DURATION = 0.45;

	constructor(root) {
		this.root = root;
		this.desktopInitialized = false;
		this.lastDesktop = this.isDesktop();

		this.slideCount = this.getSlideCount();
		this.lastActiveIndex = -1;

		this.toggleTimer = null;
		this.edgeExiting = false;
		this.edgeExitTween = null;
		this.wheelLastAt = 0;
		this.pinWheelArmed = false;
		this.pinWheelSawEvent = false;
		this.pinWheelLastAbs = 0;
		this.pinWheelLastSign = 0;
		this.lastWheelIsTrackpad = null;
		this.stepWheelLocked = false;
		this.stepWheelLockedAt = 0;
		this.edgeExitReady = false;
		this.animatingWatchdog = null;

		this.handleViewportToggle = this.handleViewportToggle.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handleEdgeWheel = this.handleEdgeWheel.bind(this);
		this.handlePageShow = this.handlePageShow.bind(this);
		this.handleLoad = this.handleLoad.bind(this);

		if (this.lastDesktop) {
			this.initDesktop();
		}

		window.addEventListener("resize", this.handleViewportToggle);
		window.addEventListener("orientationchange", this.handleViewportToggle);
		window.addEventListener("pageshow", this.handlePageShow);
		window.addEventListener("load", this.handleLoad);
	}

	isDesktop() {
		return window.innerWidth >= SolutionScrollAccordion.DESKTOP_BREAKPOINT;
	}

	getSlideCount() {
		const raw = this.root && this.root.dataset ? Number(this.root.dataset.slideCount) : 0;
		if (Number.isFinite(raw) && raw > 0) {
			return raw;
		}
		const items = this.root ? this.root.querySelectorAll(".js-ssa-item") : [];
		return items ? items.length : 0;
	}

	cacheElements() {
		this.pinEl = this.root.querySelector(".solutionScrollAccordion__pin");
		this.gridEl = this.root.querySelector(".solutionScrollAccordion__grid");
		this.leftEl = this.root.querySelector(".solutionScrollAccordion__left");
		this.items = Array.from(this.root.querySelectorAll(".js-ssa-item"));
		this.descs = Array.from(this.root.querySelectorAll(".js-ssa-desc"));
		this.mediaItems = Array.from(this.root.querySelectorAll(".js-ssa-media"));
		this.swiperEl = this.root.querySelector(".js-ssa-swiper");
	}

	handleViewportToggle() {
		if (this.toggleTimer) {
			window.clearTimeout(this.toggleTimer);
			this.toggleTimer = null;
		}
		this.toggleTimer = window.setTimeout(() => {
			this.toggleTimer = null;
			const nowDesktop = this.isDesktop();
			if (nowDesktop && !this.desktopInitialized) {
				this.initDesktop();
			} else if (!nowDesktop && this.desktopInitialized) {
				this.destroyDesktop();
			}
			this.lastDesktop = nowDesktop;
		}, 150);
	}

	initDesktop() {
		if (this.desktopInitialized || !this.isDesktop() || !this.root) {
			return;
		}

		this.cacheElements();
		this.slideCount = this.getSlideCount();

		if (!this.pinEl || !this.swiperEl || this.slideCount <= 0 || !this.items.length) {
			return;
		}

		this.root.classList.add("is-ssa-init");
		this.updateSceneHeight();
		this.setInitialState();
		this.centerContent();
		this.initSwiper();
		this.initPin();
		this.bindEdgeWheel();

		window.addEventListener("resize", this.handleResize);
		window.addEventListener("orientationchange", this.handleResize);
		this.desktopInitialized = true;

		try {
			requestAnimationFrame(() => ScrollTrigger.refresh(true));
		} catch (e) {}
	}

	destroyDesktop() {
		if (!this.desktopInitialized) {
			return;
		}

		window.removeEventListener("resize", this.handleResize);
		window.removeEventListener("orientationchange", this.handleResize);
		this.unbindEdgeWheel();

		if (this.pinTrigger && typeof this.pinTrigger.kill === "function") {
			try {
				this.pinTrigger.kill(true);
			} catch (e) {}
		}
		this.pinTrigger = null;

		if (this.swiper && typeof this.swiper.destroy === "function") {
			try {
				this.swiper.destroy(true, true);
			} catch (e) {}
		}
		this.swiper = null;

		this.root.classList.remove("is-ssa-init");
		this.root.classList.remove("is-fixed");
		this.root.style.minHeight = "";
		if (this.gridEl) {
			this.gridEl.style.paddingTop = "";
		}
		this.edgeExiting = false;
		this.edgeExitTween = null;
		this.wheelLastAt = 0;
		this.edgeExitReady = false;
		if (this.animatingWatchdog) {
			clearTimeout(this.animatingWatchdog);
			this.animatingWatchdog = null;
		}

		this.desktopInitialized = false;
	}

	handleLoad() {
		if (!this.desktopInitialized) {
			return;
		}
		this.handleResize();
		try {
			ScrollTrigger.refresh(true);
		} catch (e) {}
	}

	handlePageShow() {
		if (!this.desktopInitialized) {
			return;
		}
		try {
			ScrollTrigger.refresh(true);
		} catch (e) {}
	}

	setInitialState() {
		this.descs.forEach((desc) => {
			if (!(desc instanceof HTMLElement)) {
				return;
			}
			desc.style.overflow = "hidden";
			gsap.set(desc, { height: 0, opacity: 0 });
		});

		this.mediaItems.forEach((media) => {
			if (!(media instanceof HTMLElement)) {
				return;
			}
			gsap.set(media, { opacity: 0, visibility: "hidden" });
			media.classList.remove("is-active");
		});

		this.setActive(0, true);
	}

	initSwiper() {
		if (this.swiper && typeof this.swiper.destroy === "function") {
			try {
				this.swiper.destroy(true, true);
			} catch (e) {}
		}

		this.swiper = new Swiper(this.swiperEl, {
			direction: "vertical",
			slidesPerView: 1,
			allowTouchMove: false,
			speed: SolutionScrollAccordion.SWIPER_SPEED,
			mousewheel: {
				eventsTarget: this.root,
				forceToAxis: true,
				releaseOnEdges: true,
				sensitivity: SolutionScrollAccordion.WHEEL_SENSITIVITY,
				thresholdDelta: SolutionScrollAccordion.WHEEL_THRESHOLD_DELTA,
				thresholdTime: SolutionScrollAccordion.WHEEL_THRESHOLD_TIME,
			},
			on: {
				init: () => this.setActive(0, true),
				slideChange: () => this.setActive(this.swiper.activeIndex),
				slideChangeTransitionStart: () => {
					const inFixed = this.root && this.root.classList.contains("is-fixed");
					const now = Date.now();
					const sinceWheel = now - (this.wheelLastAt || 0);

					if (this.animatingWatchdog) {
						clearTimeout(this.animatingWatchdog);
					}
					this.animatingWatchdog = window.setTimeout(() => {
						this.animatingWatchdog = null;
						if (!this.swiper) return;
						if (this.swiper.animating) {
							try {
								this.swiper.animating = false;
							} catch (e) {}
							try {
								if (this.swiper.mousewheel) {
									this.swiper.mousewheel.enable();
								}
							} catch (e) {}
							this.stepWheelLocked = false;
							this.stepWheelLockedAt = 0;
							this.pinWheelArmed = false;
							this.pinWheelSawEvent = false;
							this.pinWheelLastAbs = 0;
							this.pinWheelLastSign = 0;
							this.edgeExiting = false;
						}
					}, SolutionScrollAccordion.SWIPER_SPEED + 400);

					if (!inFixed) {
						return;
					}
					if (this.pinWheelArmed || this.edgeExiting) {
						return;
					}
					if (sinceWheel < SolutionScrollAccordion.STEP_LOCK_MAX_MS) {
						this.stepWheelLocked = true;
						this.stepWheelLockedAt = now;
						try {
							if (this.swiper && this.swiper.mousewheel) {
								this.swiper.mousewheel.disable();
							}
						} catch (e) {}
					}
				},
				slideChangeTransitionEnd: () => {
					const last = Math.max(0, (this.slideCount || 1) - 1);
					const atEdge = this.swiper.activeIndex <= 0 || this.swiper.activeIndex >= last;
					this.edgeExitReady = Boolean(atEdge);
					if (this.animatingWatchdog) {
						clearTimeout(this.animatingWatchdog);
						this.animatingWatchdog = null;
					}
				},
			},
		});

		try {
			if (this.swiper && this.swiper.mousewheel) {
				this.swiper.mousewheel.disable();
			}
		} catch (e) {}
	}

	handleResize() {
		this.updateSceneHeight();
		this.centerContent();
		if (this.pinTrigger) {
			try {
				this.pinTrigger.refresh();
			} catch (e) {}
		} else {
			this.initPin();
		}
	}

	updateSceneHeight() {
		if (!this.root) {
			return;
		}
		const vh = window.innerHeight || 900;
		this.root.style.minHeight = `${vh}px`;
	}

	centerContent() {
		if (!this.gridEl || !this.leftEl || !this.descs.length) {
			return;
		}
		const vh = window.innerHeight || 900;

		// Find the tallest description via scrollHeight (works even when collapsed)
		let maxDescHeight = 0;
		this.descs.forEach((desc) => {
			maxDescHeight = Math.max(maxDescHeight, desc.scrollHeight);
		});

		// Temporarily collapse all descriptions to measure base content height
		const savedHeights = this.descs.map((desc) => desc.style.height);
		this.descs.forEach((desc) => {
			desc.style.height = "0px";
		});
		const baseHeight = this.leftEl.offsetHeight;
		this.descs.forEach((desc, i) => {
			desc.style.height = savedHeights[i];
		});

		// Total max height = base (all closed) + tallest description
		const maxContentHeight = baseHeight + maxDescHeight;

		// Center within the area below the sticky header (90px)
		const headerHeight = 90;
		const availableHeight = vh - headerHeight;
		const minPadding = headerHeight + 10;
		const idealPadding = Math.round(headerHeight + (availableHeight - maxContentHeight) / 2);
		this.gridEl.style.paddingTop = `${Math.max(minPadding, idealPadding)}px`;
	}

	initPin() {
		if (!this.root || !this.pinEl) {
			return;
		}
		if (this.pinTrigger && typeof this.pinTrigger.kill === "function") {
			try {
				this.pinTrigger.kill(true);
			} catch (e) {}
			this.pinTrigger = null;
		}

		this.pinTrigger = ScrollTrigger.create({
			trigger: this.root,
			start: "top top",
			end: () => {
				const vh = window.innerHeight || 900;
				const count = Math.max(1, Number(this.slideCount) || 1);
				return `+=${count * vh}`;
			},
			pin: this.pinEl,
			pinSpacing: true,
			anticipatePin: 1,
			invalidateOnRefresh: true,
			onEnter: () => {
				if (this.root.classList.contains("is-fixed")) {
					return;
				}
				this.root.classList.add("is-fixed");
				this.edgeExiting = false;
				this.pinWheelArmed = true;
				this.pinWheelSawEvent = false;
				this.pinWheelLastAbs = 0;
				this.pinWheelLastSign = 0;
				this.stepWheelLocked = false;
				this.stepWheelLockedAt = 0;
				this.edgeExitReady = false;
				try {
					if (this.swiper && this.swiper.mousewheel) {
						this.swiper.mousewheel.disable();
					}
				} catch (e) {}
			},
			onEnterBack: () => {
				if (this.root.classList.contains("is-fixed")) {
					return;
				}
				this.root.classList.add("is-fixed");
				this.edgeExiting = false;
				this.pinWheelArmed = true;
				this.pinWheelSawEvent = false;
				this.pinWheelLastAbs = 0;
				this.pinWheelLastSign = 0;
				this.stepWheelLocked = false;
				this.stepWheelLockedAt = 0;
				this.edgeExitReady = false;
				try {
					if (this.swiper && this.swiper.mousewheel) {
						this.swiper.mousewheel.disable();
					}
				} catch (e) {}
			},
			onLeave: () => {
				this.root.classList.remove("is-fixed");
				this.edgeExiting = false;
				this.pinWheelArmed = false;
				this.pinWheelSawEvent = false;
				this.pinWheelLastAbs = 0;
				this.pinWheelLastSign = 0;
				this.stepWheelLocked = false;
				this.stepWheelLockedAt = 0;
				this.edgeExitReady = false;
				this.setActive(Math.max(0, (this.slideCount || 1) - 1), true);
				try {
					if (this.swiper && this.swiper.mousewheel) {
						this.swiper.mousewheel.disable();
					}
				} catch (e) {}
			},
			onLeaveBack: () => {
				this.root.classList.remove("is-fixed");
				this.edgeExiting = false;
				this.pinWheelArmed = false;
				this.pinWheelSawEvent = false;
				this.pinWheelLastAbs = 0;
				this.pinWheelLastSign = 0;
				this.stepWheelLocked = false;
				this.stepWheelLockedAt = 0;
				this.edgeExitReady = false;
				this.setActive(0, true);
				try {
					if (this.swiper && this.swiper.mousewheel) {
						this.swiper.mousewheel.disable();
					}
				} catch (e) {}
			},
		});
	}

	bindEdgeWheel() {
		if (!this.root) {
			return;
		}
		this.root.addEventListener("wheel", this.handleEdgeWheel, { passive: false, capture: true });
	}

	unbindEdgeWheel() {
		if (!this.root) {
			return;
		}
		this.root.removeEventListener("wheel", this.handleEdgeWheel, true);
	}

	handleEdgeWheel(event) {
		if (!event || !this.root || !this.root.classList.contains("is-fixed")) {
			return;
		}
		if (!this.swiper || !this.pinTrigger) {
			return;
		}
		const now = Date.now();
		const isNewGesture = !this.wheelLastAt || now - this.wheelLastAt > SolutionScrollAccordion.GESTURE_GAP_MS;
		this.wheelLastAt = now;

		const dy = Number(event.deltaY || 0);
		const absDy = Number.isFinite(dy) ? Math.abs(dy) : 0;
		const sign = dy > 0 ? 1 : dy < 0 ? -1 : 0;
		const isTrackpad = event.deltaMode === 0;

		this.applySwiperWheelTuning(isTrackpad);

		if (this.stepWheelLocked) {
			const dirFlip = Boolean(this.pinWheelLastSign && sign && sign !== this.pinWheelLastSign);
			const prevAbs = this.pinWheelLastAbs || absDy;
			const spike = absDy >= Math.max(SolutionScrollAccordion.PIN_ARM_SPIKE_MIN_DELTA, prevAbs * SolutionScrollAccordion.PIN_ARM_SPIKE_MULT);
			const lockedFor = now - (this.stepWheelLockedAt || 0);
			const canUnlockByTime = lockedFor >= SolutionScrollAccordion.STEP_LOCK_MIN_MS;
			this.pinWheelLastAbs = absDy;
			this.pinWheelLastSign = sign;

			if (canUnlockByTime && (isNewGesture || dirFlip || spike)) {
				this.stepWheelLocked = false;
				this.stepWheelLockedAt = 0;
				this.pinWheelLastAbs = 0;
				this.pinWheelLastSign = 0;
				try {
					if (this.swiper && this.swiper.mousewheel) {
						this.swiper.mousewheel.enable();
					}
				} catch (e) {}
				return;
			}

			event.preventDefault();
			try {
				event.stopImmediatePropagation();
			} catch (e) {
				event.stopPropagation();
			}
			return;
		}

		// Block Swiper during the pin-enter gesture (including its inertial tail),
		// then enable Swiper on the *next* gesture so slide 1 is actually visible.
		if (this.pinWheelArmed) {
			const dirFlip = Boolean(this.pinWheelLastSign && sign && sign !== this.pinWheelLastSign);
			const prevAbs = this.pinWheelLastAbs || absDy;
			const spike = absDy >= Math.max(SolutionScrollAccordion.PIN_ARM_SPIKE_MIN_DELTA, prevAbs * SolutionScrollAccordion.PIN_ARM_SPIKE_MULT);
			this.pinWheelLastAbs = absDy;
			this.pinWheelLastSign = sign;

			if (this.pinWheelSawEvent && (isNewGesture || dirFlip || spike)) {
				this.pinWheelArmed = false;
				this.pinWheelSawEvent = false;
				this.stepWheelLocked = false;
				this.stepWheelLockedAt = 0;
				this.pinWheelLastAbs = 0;
				this.pinWheelLastSign = 0;
				try {
					if (this.swiper && this.swiper.mousewheel) {
						this.swiper.mousewheel.enable();
					}
				} catch (e) {}
				// Allow this new gesture to be handled by Swiper.
				return;
			}
			this.pinWheelSawEvent = true;
			event.preventDefault();
			try {
				event.stopImmediatePropagation();
			} catch (e) {
				event.stopPropagation();
			}
			return;
		}
		if (this.edgeExiting) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (!Number.isFinite(dy) || dy === 0) {
			return;
		}
		const isFirst = this.swiper.activeIndex <= 0;
		const isLast = this.swiper.activeIndex >= Math.max(0, (this.slideCount || 1) - 1);
		const outwardDown = isLast && dy > 0;
		const outwardUp = isFirst && dy < 0;
		if (!outwardDown && !outwardUp) {
			return;
		}

		// Never exit while Swiper is transitioning to the edge slide, and require that
		// the edge slide has fully settled before the next outward wheel exits.
		if (this.swiper && this.swiper.animating) {
			event.preventDefault();
			return;
		}
		if (!this.edgeExitReady) {
			event.preventDefault();
			return;
		}

		event.preventDefault();
		this.edgeExiting = true;
		this.edgeExitReady = false;

		try {
			if (this.swiper && this.swiper.mousewheel) {
				this.swiper.mousewheel.disable();
			}
		} catch (e) {}

		const targetY = outwardDown ? Number(this.pinTrigger.end) + 2 : Number(this.pinTrigger.start) - 2;
		const y = Number.isFinite(targetY) ? targetY : window.scrollY || 0;
		// Instant jump avoids wheel/inertia fighting a tween (screen shake).
		try {
			window.scrollTo(0, y);
		} catch (e) {}
	}

	applySwiperWheelTuning(isTrackpad) {
		if (!this.swiper || !this.swiper.params || !this.swiper.params.mousewheel) {
			return;
		}
		if (this.lastWheelIsTrackpad === isTrackpad) {
			return;
		}
		this.lastWheelIsTrackpad = isTrackpad;

		const mw = this.swiper.params.mousewheel;
		const base = this.swiper.originalParams && this.swiper.originalParams.mousewheel ? this.swiper.originalParams.mousewheel : null;

		if (isTrackpad) {
			mw.thresholdDelta = SolutionScrollAccordion.TRACKPAD_WHEEL_THRESHOLD_DELTA;
			mw.thresholdTime = SolutionScrollAccordion.TRACKPAD_WHEEL_THRESHOLD_TIME;
			mw.sensitivity = SolutionScrollAccordion.TRACKPAD_WHEEL_SENSITIVITY;
			if (base) {
				base.thresholdDelta = SolutionScrollAccordion.TRACKPAD_WHEEL_THRESHOLD_DELTA;
				base.thresholdTime = SolutionScrollAccordion.TRACKPAD_WHEEL_THRESHOLD_TIME;
				base.sensitivity = SolutionScrollAccordion.TRACKPAD_WHEEL_SENSITIVITY;
			}
			return;
		}

		mw.thresholdDelta = SolutionScrollAccordion.WHEEL_THRESHOLD_DELTA;
		mw.thresholdTime = SolutionScrollAccordion.WHEEL_THRESHOLD_TIME;
		mw.sensitivity = SolutionScrollAccordion.WHEEL_SENSITIVITY;
		if (base) {
			base.thresholdDelta = SolutionScrollAccordion.WHEEL_THRESHOLD_DELTA;
			base.thresholdTime = SolutionScrollAccordion.WHEEL_THRESHOLD_TIME;
			base.sensitivity = SolutionScrollAccordion.WHEEL_SENSITIVITY;
		}
	}

	setActive(index, force = false) {
		const total = Math.max(1, this.slideCount || this.items.length || 1);
		const nextIndex = Math.max(0, Math.min(Number(index) || 0, total - 1));
		const prevIndex = this.lastActiveIndex;

		if (!force && prevIndex === nextIndex) {
			return;
		}

		this.items.forEach((item) => {
			const itemIndex = Number(item.dataset.index);
			item.classList.toggle("is-active", itemIndex === nextIndex);
		});

		const prevItem = this.items.find((el) => Number(el.dataset.index) === prevIndex) || null;
		const nextItem = this.items.find((el) => Number(el.dataset.index) === nextIndex) || null;

		const prevDesc = prevItem ? prevItem.querySelector(".js-ssa-desc") : null;
		const nextDesc = nextItem ? nextItem.querySelector(".js-ssa-desc") : null;

		if (prevDesc && prevDesc !== nextDesc) {
			gsap.killTweensOf(prevDesc);
			if (force) {
				gsap.set(prevDesc, { height: 0, opacity: 0 });
			} else {
				gsap.to(prevDesc, {
					height: 0,
					opacity: 0,
					duration: SolutionScrollAccordion.DESC_CLOSE_DURATION,
					ease: "power2.inOut",
					overwrite: true,
				});
			}
		}

		if (nextDesc) {
			gsap.killTweensOf(nextDesc);
			if (force) {
				gsap.set(nextDesc, { height: "auto", opacity: 1 });
			} else {
				gsap.to(nextDesc, {
					height: "auto",
					opacity: 1,
					duration: SolutionScrollAccordion.DESC_OPEN_DURATION,
					ease: "power2.out",
					overwrite: true,
				});
			}
		}

		const prevMedia = this.mediaItems.find((el) => Number(el.dataset.index) === prevIndex) || null;
		const nextMedia = this.mediaItems.find((el) => Number(el.dataset.index) === nextIndex) || null;

		if (prevMedia && prevMedia !== nextMedia) {
			gsap.killTweensOf(prevMedia);
			prevMedia.classList.remove("is-active");
			if (force) {
				prevMedia.style.visibility = "hidden";
				gsap.set(prevMedia, { opacity: 0 });
			} else {
				gsap.to(prevMedia, {
					opacity: 0,
					duration: SolutionScrollAccordion.MEDIA_FADE_DURATION,
					ease: "power2.out",
					overwrite: true,
					onComplete: () => {
						try {
							prevMedia.style.visibility = "hidden";
						} catch (e) {}
					},
				});
			}
		}

		if (nextMedia) {
			gsap.killTweensOf(nextMedia);
			nextMedia.classList.add("is-active");
			try {
				nextMedia.style.visibility = "visible";
				nextMedia.style.zIndex = "2";
			} catch (e) {}
			if (force) {
				gsap.set(nextMedia, { opacity: 1 });
			} else {
				gsap.to(nextMedia, {
					opacity: 1,
					duration: SolutionScrollAccordion.MEDIA_FADE_DURATION,
					ease: "power2.out",
					overwrite: true,
				});
			}
		}

		this.lastActiveIndex = nextIndex;
	}
}

document.addEventListener("DOMContentLoaded", () => {
	const root = document.querySelector(".solutionScrollAccordion");
	if (!root) {
		return;
	}
	root.SolutionScrollAccordion = new SolutionScrollAccordion(root);
});
